import { useState, type FormEvent, type ReactNode } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useWorkspaceSettings } from '@/hooks/queries/useWorkspaceSettings';
import { useWorkspaceApiKey } from '@/hooks/mutations/useWorkspaceApiKey';
import { userRoleLabel } from '@/lib/permissions';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Stagger } from '@/components/primitives/Stagger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function SettingsScreen() {
  const { t, locale } = useI18n();
  const settings = useWorkspaceSettings();
  const { save, remove } = useWorkspaceApiKey();
  const [key, setKey] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const busy = save.isPending || remove.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    remove.reset();
    save.mutate(key.trim(), {
      onSuccess: () => {
        setKey('');
      },
    });
  }

  const data = settings.data;
  const date = (value: string) => new Date(value).toLocaleString(locale);
  return (
    <Stagger className="mx-auto w-full max-w-3xl space-y-5">
      <header className="space-y-1">
        <h1 className="text-[24px] font-semibold tracking-tight text-text-1">
          {t('Paramètres et intégrations', 'Settings and integrations')}
        </h1>
        <p className="text-[13px] text-text-3">
          {t(
            'Gérez les connexions et les paramètres de votre espace.',
            'Manage connections and settings for your workspace.',
          )}
        </p>
      </header>

      {settings.error ? (
        <ErrorInline error={settings.error} onRetry={() => settings.refetch()} />
      ) : !data ? (
        <div aria-label={t('Chargement des paramètres', 'Loading settings')} className="space-y-4">
          <Skeleton className="h-36 w-full rounded-[14px]" />
          <Skeleton className="h-64 w-full rounded-[14px]" />
        </div>
      ) : (
        <>
          <SettingsCard title={t('Mon espace', 'My workspace')}>
            <dl className="grid gap-4 text-[13px] sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-text-3">{t('Identifiant', 'Username')}</dt>
                <dd className="mt-1 break-all font-medium">{data.username}</dd>
              </div>
              <div>
                <dt className="text-text-3">{t('Rôle', 'Role')}</dt>
                <dd className="mt-1 font-medium">{userRoleLabel(data.role, t)}</dd>
              </div>
              <div>
                <dt className="text-text-3">
                  {t('Comptes actifs / plafond', 'Active accounts / limit')}
                </dt>
                <dd className="mt-1 font-medium">
                  {data.activeAccounts} /{' '}
                  {data.maxActiveAccounts ?? t('Sans plafond propre', 'No workspace limit')}
                </dd>
              </div>
            </dl>
            <p className="text-[12px] text-text-3">
              {t(
                'Ces réglages s’appliquent uniquement à votre espace.',
                'These settings apply only to your workspace.',
              )}
            </p>
          </SettingsCard>

          <SettingsCard title="RapidAPI">
            <p className="text-[13px] text-text-3">
              {t(
                'La clé de votre espace permet la collecte des statistiques Instagram.',
                'Your workspace key enables Instagram statistics collection.',
              )}
            </p>
            <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
              <div>
                <dt className="text-text-3">{t('État', 'Status')}</dt>
                <dd className="mt-1 font-medium">
                  {data.rapidApi.status === 'ABSENT'
                    ? t('Non configurée', 'Not configured')
                    : data.rapidApi.status === 'REJECTED'
                      ? t('Clé refusée — à remplacer', 'Key rejected — replace it')
                      : t('Configurée', 'Configured')}
                </dd>
              </div>
              {data.rapidApi.masked && (
                <div>
                  <dt className="text-text-3">{t('Clé actuelle', 'Current key')}</dt>
                  <dd className="mt-1 font-mono">{data.rapidApi.masked}</dd>
                </div>
              )}
              {data.rapidApi.quotaRemaining !== null && (
                <div>
                  <dt className="text-text-3">
                    {t('Quota restant observé', 'Last observed remaining quota')}
                  </dt>
                  <dd className="mt-1">{data.rapidApi.quotaRemaining.toLocaleString(locale)}</dd>
                </div>
              )}
              {data.rapidApi.lastValidatedAt && (
                <div>
                  <dt className="text-text-3">{t('Dernière validation', 'Last validated')}</dt>
                  <dd className="mt-1">{date(data.rapidApi.lastValidatedAt)}</dd>
                </div>
              )}
              {data.rapidApi.lastRejectedAt && (
                <div>
                  <dt className="text-text-3">{t('Dernier refus', 'Last rejected')}</dt>
                  <dd className="mt-1">{date(data.rapidApi.lastRejectedAt)}</dd>
                </div>
              )}
            </dl>
            {data.rapidApi.pausedAccounts > 0 && (
              <p className="text-[13px] text-rose">
                {t(
                  `${data.rapidApi.pausedAccounts} compte(s) en pause faute de clé valide.`,
                  `${data.rapidApi.pausedAccounts} account(s) paused because a valid key is required.`,
                )}
              </p>
            )}
            <form onSubmit={submit} className="space-y-3 border-t border-line-1 pt-4">
              <div className="space-y-2">
                <Label htmlFor="rapid-api-key">
                  {t('Nouvelle clé RapidAPI', 'New RapidAPI key')}
                </Label>
                <Input
                  id="rapid-api-key"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={256}
                  required
                  value={key}
                  disabled={busy}
                  onChange={(event) => {
                    setKey(event.target.value);
                    save.reset();
                  }}
                  aria-describedby="rapid-api-help"
                />
                <p id="rapid-api-help" className="text-[12px] text-text-3">
                  {t(
                    'La nouvelle clé est vérifiée avant de remplacer la clé actuelle.',
                    'The new key is checked before replacing the current key.',
                  )}
                </p>
              </div>
              {save.error && (
                <div role="alert">
                  <ErrorInline error={save.error} />
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="submit" variant="primary" disabled={busy || key.trim().length < 8}>
                  {save.isPending
                    ? t('Vérification…', 'Checking…')
                    : t('Enregistrer la clé', 'Save key')}
                </Button>
                {data.rapidApi.status !== 'ABSENT' && (
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      remove.reset();
                      setConfirmRemove(true);
                    }}
                  >
                    {t('Retirer la clé', 'Remove key')}
                  </Button>
                )}
              </div>
            </form>
          </SettingsCard>

          <SettingsCard title="GetMySocial">
            <p className="text-[13px] font-medium">
              {data.getMySocialConfigured
                ? t('Connexion configurée', 'Connection configured')
                : t('Connexion non configurée', 'Connection not configured')}
            </p>
            <p className="text-[13px] text-text-3">
              {t(
                'Connexion gérée par la plateforme. Les statistiques de conversion affichées concernent uniquement les comptes de votre espace.',
                'Connection managed by the platform. Conversion statistics are limited to accounts in your workspace.',
              )}
            </p>
          </SettingsCard>
        </>
      )}

      <Dialog
        open={confirmRemove}
        onOpenChange={(open) => {
          if (!remove.isPending) setConfirmRemove(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Retirer la clé RapidAPI ?', 'Remove the RapidAPI key?')}</DialogTitle>
            <DialogDescription>
              {t(
                'La collecte des comptes de votre espace sera mise en pause jusqu’à l’ajout d’une clé valide. Les statistiques existantes seront conservées.',
                'Collection for your workspace accounts will pause until a valid key is added. Existing statistics will be kept.',
              )}
            </DialogDescription>
          </DialogHeader>
          {remove.error && (
            <div role="alert">
              <ErrorInline error={remove.error} />
            </div>
          )}
          <DialogFooter>
            <Button disabled={busy} onClick={() => setConfirmRemove(false)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmRemove(false);
                    setKey('');
                    save.reset();
                  },
                })
              }
            >
              {remove.isPending ? t('Retrait…', 'Removing…') : t('Retirer la clé', 'Remove key')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stagger>
  );
}

function SettingsCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="min-w-0 space-y-4 rounded-[14px] border border-line-1 bg-bg-1 p-4 text-text-1 shadow-card sm:p-5">
      <h2 className="text-[16px] font-semibold">{title}</h2>
      {children}
    </section>
  );
}
